import { Card, Col, Row, Skeleton, Tag, Typography } from "antd";
import { Moment } from "moment";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { InstitutionDetails } from "@/providers/smarta-banking/banking-api-client";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

const getTexts = (t: TFunction) => ({
  title: t("connectionSummaryCard.title"),
  fields: {
    institution: {
      label: t("connectionSummaryCard.fields.institution.label"),
    },
    accessValidFor: {
      label: t("connectionSummaryCard.fields.accessValidFor.label"),
      value: (days: number) =>
        t("connectionSummaryCard.fields.accessValidFor.value", { days }),
    },
    accountSelection: {
      label: t("connectionSummaryCard.fields.accountSelection.label"),
      options: {
        yes: t("connectionSummaryCard.fields.accountSelection.options.yes"),
        no: t("connectionSummaryCard.fields.accountSelection.options.no"),
      },
    },
    historicalStartDate: {
      label: t("connectionSummaryCard.fields.historicalStartDate.label"),
    },
  },
});

type Props = {
  isLoading: boolean;
  institution?: InstitutionDetails;
  historicalStartDate?: Moment;
};

export const ConnectionSummaryCard = ({
  isLoading,
  institution,
  historicalStartDate,
}: Props) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);

  const isAccountSelectionSupported =
    institution?.supported_features.includes("account_selection");
  return (
    <Card title={texts.title} size="small">
      <Row className="mb-2" align="middle">
        <Col span={8}>
          <Typography.Text strong>
            {texts.fields.institution.label}
          </Typography.Text>
        </Col>
        <Col span={16}>
          <Row align="middle" gutter={8}>
            <Col>
              {isLoading || !institution ? (
                <Skeleton.Avatar active size={32} shape="circle" />
              ) : (
                <img
                  src={institution.logo}
                  alt={institution.name}
                  style={{ width: 32, height: 32, objectFit: "contain" }}
                />
              )}
            </Col>
            <Col>
              {isLoading || !institution ? (
                <Skeleton.Input active size="small" />
              ) : (
                <Typography.Title level={5} className="mb-0">
                  {institution.name}
                </Typography.Title>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={8}>
          <Typography.Text strong>
            {texts.fields.accessValidFor.label}
          </Typography.Text>
        </Col>
        <Col span={16}>
          {isLoading || !institution ? (
            <Skeleton.Input active size="small" />
          ) : (
            texts.fields.accessValidFor.value(
              institution.max_access_valid_for_days
            )
          )}
        </Col>
      </Row>
      <Row className="mb-2">
        <Col span={8}>
          <Typography.Text strong>
            {texts.fields.accountSelection.label}
          </Typography.Text>
        </Col>
        <Col span={16}>
          <Row>
            {isLoading || !institution ? (
              <Skeleton.Input active size="small" />
            ) : isAccountSelectionSupported ? (
              <Tag color="green">
                {texts.fields.accountSelection.options.yes}
              </Tag>
            ) : (
              <Tag color="orange">
                {texts.fields.accountSelection.options.no}
              </Tag>
            )}
          </Row>
        </Col>
      </Row>

      <Row>
        <Col span={8}>
          <Typography.Text strong>
            {texts.fields.historicalStartDate.label}
          </Typography.Text>
        </Col>
        <Col span={16}>
          {historicalStartDate?.format(DISPLAY_DATE_FORMAT) ?? "-"}
        </Col>
      </Row>
    </Card>
  );
};
